import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../../api.ts';
import { formatEuros } from '../../format.ts';
import type { Invoice } from '../../components/InvoiceView.tsx';

interface Referrer {
  id: string;
  referral_code: string;
  company_name: string | null;
  siret: string | null;
  status: 'ACTIVE' | 'SUSPENDED';
  commission_share_override_pct: string | null;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  referrals_count: number;
  held_cents: number;
  due_cents: number;
  invoiced_cents: number;
  stripe_connected: boolean;
  paid_cents: number;
}

interface Commission {
  id: string;
  referrer_name: string;
  extra_first_name: string;
  mission_title: string;
  amount_cents: number;
  platform_fee_cents: number;
  share_pct: string;
  effective_status: 'HELD' | 'DUE' | 'INVOICED' | 'PAID' | 'REVERSED';
  eligible_at: string;
  paid_at: string | null;
  paid_reference: string | null;
  created_at: string;
}

const COMMISSION_LABELS: Record<Commission['effective_status'], string> = {
  HELD: 'En carence',
  DUE: 'Payable',
  INVOICED: 'Facturée',
  PAID: 'Payée',
  REVERSED: 'Annulée',
};

const COMMISSION_BADGES: Record<Commission['effective_status'], string> = {
  HELD: 'badge-pending',
  DUE: 'badge-available',
  INVOICED: 'badge-pending',
  PAID: 'badge-neutral',
  REVERSED: 'badge-unavailable',
};

const ERROR_LABELS: Record<string, string> = {
  EMAIL_TAKEN: 'Cet e-mail est déjà utilisé.',
  INVALID_EMAIL: 'E-mail invalide.',
  INVALID_SHARE_PCT: 'Le taux doit être compris entre 0 et 100.',
  MISSING_FIELDS: 'Prénom, nom et e-mail sont obligatoires.',
};

function InviteForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', companyName: '', siret: '', commissionSharePct: '' });
  const [link, setLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setLink(null);
    try {
      const res = await api.post<{ activationUrl: string }>('/admin/referrers/invite', {
        ...form,
        commissionSharePct: form.commissionSharePct === '' ? null : Number(form.commissionSharePct),
      });
      setLink(res.activationUrl);
      setForm({ email: '', firstName: '', lastName: '', companyName: '', siret: '', commissionSharePct: '' });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? ERROR_LABELS[err.code] ?? 'Invitation impossible.' : 'Invitation impossible.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
      <div style={{ fontWeight: 800 }}>Inviter un apporteur d'affaires</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        Aucun e-mail n'est envoyé automatiquement : copiez le lien d'activation généré et transmettez-le vous-même. Il est valable 7 jours.
      </div>
      {error && <div className="error-banner">{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
        <input className="input" required placeholder="Prénom" value={form.firstName} onChange={(e) => set({ firstName: e.target.value })} />
        <input className="input" required placeholder="Nom" value={form.lastName} onChange={(e) => set({ lastName: e.target.value })} />
        <input className="input" required type="email" placeholder="E-mail" value={form.email} onChange={(e) => set({ email: e.target.value })} />
        <input className="input" placeholder="Société (facturation)" value={form.companyName} onChange={(e) => set({ companyName: e.target.value })} />
        <input className="input" placeholder="SIRET" value={form.siret} onChange={(e) => set({ siret: e.target.value })} />
        <input className="input" type="number" min={0} max={100} step="0.5" placeholder="Taux spécifique % (vide = global)" value={form.commissionSharePct} onChange={(e) => set({ commissionSharePct: e.target.value })} />
      </div>
      <button className="btn btn-primary" style={{ width: 'auto', alignSelf: 'flex-start', padding: '10px 18px' }} disabled={busy}>
        {busy ? 'Création…' : 'Créer et générer le lien'}
      </button>
      {link && <LinkBox link={link} />}
    </form>
  );
}

function LinkBox({ link }: { link: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ background: 'var(--accent-tint)', borderRadius: 'var(--radius-md)', padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
      <code style={{ fontSize: 12, wordBreak: 'break-all', flex: 1, minWidth: 220 }}>{link}</code>
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        onClick={() => navigator.clipboard.writeText(link).then(() => setCopied(true))}
      >
        {copied ? 'Copié' : 'Copier le lien'}
      </button>
    </div>
  );
}

function ReferrersTab() {
  const [referrers, setReferrers] = useState<Referrer[] | null>(null);
  const [globalPct, setGlobalPct] = useState<number | null>(null);
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const [newLink, setNewLink] = useState<{ id: string; link: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ referrers: Referrer[] }>('/admin/referrers').then((res) => setReferrers(res.referrers));
    api.get<{ settings: { referral: { defaultSharePct: number } } }>('/admin/settings').then((res) => setGlobalPct(res.settings.referral.defaultSharePct));
  }, []);

  useEffect(load, [load]);

  async function savePct() {
    if (!editing) return;
    setError(null);
    try {
      await api.patch(`/admin/referrers/${editing.id}`, { commissionSharePct: editing.value === '' ? null : Number(editing.value) });
      setEditing(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERROR_LABELS[err.code] ?? 'Modification impossible.' : 'Modification impossible.');
    }
  }

  async function toggleStatus(r: Referrer) {
    const next = r.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    if (!window.confirm(next === 'SUSPENDED' ? `Suspendre ${r.first_name} ${r.last_name} ? Il ne générera plus de commission.` : `Réactiver ${r.first_name} ${r.last_name} ?`)) return;
    await api.patch(`/admin/referrers/${r.id}`, { status: next });
    load();
  }

  async function regenerateLink(r: Referrer) {
    const res = await api.post<{ activationUrl: string }>(`/admin/referrers/${r.id}/invitation`);
    setNewLink({ id: r.id, link: res.activationUrl });
  }

  return (
    <div>
      <InviteForm onCreated={load} />
      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}
      {!referrers && <div className="empty-state">Chargement…</div>}
      {referrers && referrers.length === 0 && <div className="empty-state">Aucun apporteur d'affaires pour l'instant.</div>}
      {referrers && referrers.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Apporteur</th><th>Code</th><th>Taux</th><th>Filleuls</th><th>En carence</th><th>Payable</th><th>Facturé</th><th>Payé</th><th /></tr>
            </thead>
            <tbody>
              {referrers.map((r) => (
                <tr key={r.id} style={{ opacity: r.status === 'SUSPENDED' ? 0.55 : 1 }}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.first_name} {r.last_name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.company_name ?? r.email}</div>
                    {newLink?.id === r.id && <div style={{ marginTop: 8, minWidth: 260 }}><LinkBox link={newLink.link} /></div>}
                  </td>
                  <td><code>{r.referral_code}</code></td>
                  <td>
                    {editing?.id === r.id ? (
                      <span style={{ display: 'inline-flex', gap: 6 }}>
                        <input className="input" style={{ width: 80, padding: '6px 8px' }} type="number" min={0} max={100} step="0.5" placeholder={`${globalPct ?? ''}`} value={editing.value} onChange={(e) => setEditing({ id: r.id, value: e.target.value })} />
                        <button className="btn btn-primary btn-sm" onClick={savePct}>OK</button>
                      </span>
                    ) : (
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing({ id: r.id, value: r.commission_share_override_pct ?? '' })}>
                        {r.commission_share_override_pct !== null ? `${Number(r.commission_share_override_pct)} %` : `Global (${globalPct ?? '…'} %)`}
                      </button>
                    )}
                  </td>
                  <td>{r.referrals_count}</td>
                  <td>{formatEuros(r.held_cents)}</td>
                  <td style={{ fontWeight: 700 }}>{formatEuros(r.due_cents)}</td>
                  <td>{formatEuros(r.invoiced_cents)}</td>
                  <td>{formatEuros(r.paid_cents)}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => regenerateLink(r)}>Nouveau lien d'accès</button>{' '}
                    <button className={`btn btn-sm ${r.status === 'ACTIVE' ? 'btn-danger-outline' : 'btn-secondary'}`} onClick={() => toggleStatus(r)}>
                      {r.status === 'ACTIVE' ? 'Suspendre' : 'Réactiver'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CommissionsTab() {
  const [commissions, setCommissions] = useState<Commission[] | null>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ commissions: Commission[] }>(`/admin/commissions${status ? `?status=${status}` : ''}`).then((res) => setCommissions(res.commissions));
  }, [status]);

  useEffect(load, [load]);

  async function reverse(c: Commission) {
    const reason = window.prompt('Motif de l’annulation (obligatoire) :');
    if (!reason?.trim()) return;
    setError(null);
    try {
      await api.post(`/admin/commissions/${c.id}/reverse`, { reason });
      load();
    } catch {
      setError('Cette commission ne peut plus être annulée.');
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <select className="input" style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Toutes les commissions</option>
          {Object.entries(COMMISSION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>
      {error && <div className="error-banner" style={{ marginBottom: 12 }}>{error}</div>}
      {!commissions && <div className="empty-state">Chargement…</div>}
      {commissions && commissions.length === 0 && <div className="empty-state">Aucune commission.</div>}
      {commissions && commissions.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Apporteur</th><th>Affaire</th><th>Base</th><th>Taux</th><th>Commission</th><th>Statut</th><th>Payable le</th><th /></tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id}>
                  <td style={{ fontWeight: 700 }}>{c.referrer_name}</td>
                  <td>{c.mission_title} <span style={{ color: 'var(--text-tertiary)' }}>· {c.extra_first_name}</span></td>
                  <td>{formatEuros(c.platform_fee_cents)}</td>
                  <td>{Number(c.share_pct)} %</td>
                  <td style={{ fontWeight: 700 }}>{formatEuros(c.amount_cents)}</td>
                  <td>
                    <span className={`badge ${COMMISSION_BADGES[c.effective_status]}`}>{COMMISSION_LABELS[c.effective_status]}</span>
                    {c.paid_reference && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{c.paid_reference}</div>}
                  </td>
                  <td>{new Date(c.eligible_at).toLocaleDateString('fr-FR')}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {(c.effective_status === 'DUE' || c.effective_status === 'HELD') && <button className="btn btn-danger-outline btn-sm" onClick={() => reverse(c)}>Annuler</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const INVOICE_BADGES: Record<Invoice['status'], { label: string; badge: string }> = {
  REQUESTED: { label: 'À payer', badge: 'badge-pending' },
  PAID: { label: 'Payée', badge: 'badge-available' },
  REJECTED: { label: 'Annulée', badge: 'badge-unavailable' },
};

const INVOICE_ERRORS: Record<string, string> = {
  STRIPE_ACCOUNT_MISSING: 'Cet apporteur n\u2019a pas connecté de compte Stripe : utilisez le virement manuel.',
  STRIPE_TRANSFER_FAILED: 'Stripe a refusé le virement (fonds insuffisants ou compte non prêt). La facture reste à payer.',
  NOT_PAYABLE: 'Cette facture n\u2019est plus à payer.',
  NOT_REJECTABLE: 'Cette facture ne peut plus être rejetée.',
};

function InvoicesTab() {
  const [invoices, setInvoices] = useState<(Invoice & { stripeConnected: boolean })[] | null>(null);
  const [status, setStatus] = useState('REQUESTED');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    api.get<{ invoices: (Invoice & { stripeConnected: boolean })[] }>(`/admin/referral-invoices${status ? `?status=${status}` : ''}`).then((res) => setInvoices(res.invoices));
  }, [status]);

  useEffect(load, [load]);

  async function run(inv: Invoice, action: () => Promise<unknown>, success: string) {
    setBusyId(inv.id);
    setMessage(null);
    try {
      await action();
      setMessage({ kind: 'ok', text: success });
      load();
    } catch (err) {
      setMessage({ kind: 'error', text: err instanceof ApiError ? INVOICE_ERRORS[err.code] ?? 'Action impossible.' : 'Action impossible.' });
    } finally {
      setBusyId(null);
    }
  }

  function payStripe(inv: Invoice) {
    if (!window.confirm(`Virer ${formatEuros(inv.totalCents)} à ${inv.referrerName} via Stripe ?`)) return;
    run(inv, () => api.post(`/admin/referral-invoices/${inv.id}/pay-stripe`), `Virement Stripe effectué pour la facture ${inv.number}.`);
  }

  function payManual(inv: Invoice) {
    const reference = window.prompt(`Référence du virement de ${formatEuros(inv.totalCents)} (obligatoire) :`);
    if (!reference?.trim()) return;
    run(inv, () => api.post(`/admin/referral-invoices/${inv.id}/pay-manual`, { reference }), `Facture ${inv.number} marquée payée.`);
  }

  function reject(inv: Invoice) {
    const reason = window.prompt('Motif du rejet (obligatoire). Les commissions seront libérées :');
    if (!reason?.trim()) return;
    run(inv, () => api.post(`/admin/referral-invoices/${inv.id}/reject`, { reason }), `Facture ${inv.number} rejetée.`);
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select className="input" style={{ maxWidth: 200 }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Toutes</option>
          <option value="REQUESTED">À payer</option>
          <option value="PAID">Payées</option>
          <option value="REJECTED">Annulées</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input className="input" type="month" style={{ width: 170 }} value={month} onChange={(e) => setMonth(e.target.value)} />
          <a className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }} href={`/api/admin/referral-invoices/export.csv?month=${month}`}>Export comptable CSV</a>
        </div>
      </div>

      {message && <div className={message.kind === 'ok' ? 'badge badge-available' : 'error-banner'} style={{ padding: '10px 14px', marginBottom: 12 }}>{message.text}</div>}
      {!invoices && <div className="empty-state">Chargement…</div>}
      {invoices && invoices.length === 0 && <div className="empty-state">Aucune facture.</div>}
      {invoices && invoices.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Numéro</th><th>Apporteur</th><th>Émise le</th><th>HT</th><th>TVA</th><th>TTC</th><th>Statut</th><th /></tr></thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td style={{ fontWeight: 700 }}>{inv.number}</td>
                  <td>{inv.referrerName}<div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{inv.issuer.companyName}</div></td>
                  <td>{new Date(inv.issuedAt).toLocaleDateString('fr-FR')}</td>
                  <td>{formatEuros(inv.subtotalCents)}</td>
                  <td>{formatEuros(inv.vatCents)}</td>
                  <td style={{ fontWeight: 700 }}>{formatEuros(inv.totalCents)}</td>
                  <td>
                    <span className={`badge ${INVOICE_BADGES[inv.status].badge}`}>{INVOICE_BADGES[inv.status].label}</span>
                    {inv.paidReference && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{inv.paidReference}</div>}
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Link to={`/admin/factures/${inv.id}`} className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }}>Voir</Link>{' '}
                    {inv.status === 'REQUESTED' && (
                      <>
                        {inv.stripeConnected && <button className="btn btn-primary btn-sm" disabled={busyId === inv.id} onClick={() => payStripe(inv)}>Payer via Stripe</button>}{' '}
                        <button className="btn btn-secondary btn-sm" disabled={busyId === inv.id} onClick={() => payManual(inv)}>Virement manuel</button>{' '}
                        <button className="btn btn-danger-outline btn-sm" disabled={busyId === inv.id} onClick={() => reject(inv)}>Rejeter</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminReferrers() {
  const [tab, setTab] = useState<'referrers' | 'invoices' | 'commissions'>('referrers');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        <button className={`btn btn-sm ${tab === 'referrers' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('referrers')}>Apporteurs</button>
        <button className={`btn btn-sm ${tab === 'invoices' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('invoices')}>Factures</button>
        <button className={`btn btn-sm ${tab === 'commissions' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('commissions')}>Commissions</button>
      </div>
      {tab === 'referrers' ? <ReferrersTab /> : tab === 'invoices' ? <InvoicesTab /> : <CommissionsTab />}
    </div>
  );
}
